import { useState, useEffect } from "react";
import { Loader2, User as UserIcon, ChevronDown, ChevronRight } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import * as Sentry from "@sentry/react";

interface Profile {
  id: string;
  user_id: string;
  email: string;
  full_name: string | null;
  created_at: string;
}

interface MembershipDetail {
  id: string;
  workspace_id: string;
  role: string;
  workspace_name?: string;
}

export default function Users() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [expandedUser, setExpandedUser] = useState<string | null>(null);
  const [memberships, setMemberships] = useState<Record<string, MembershipDetail[]>>({});

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    const { data, error } = await supabase
      .from("profiles")
      .select("*")
      .eq("is_deleted", false)
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Failed to load profiles:", error);
      Sentry.captureException(error);
      setLoading(false);
      return;
    }
    setProfiles(data || []);
    setLoading(false);
  };

  const loadUserMemberships = async (userId: string) => {
    if (memberships[userId]) {
      setExpandedUser(expandedUser === userId ? null : userId);
      return;
    }

    const { data, error } = await supabase
      .from("workspace_memberships")
      .select("id, workspace_id, role")
      .eq("user_id", userId)
      .eq("status", "active")
      .eq("is_deleted", false);

    if (error) {
      console.error("Failed to load memberships:", error);
      Sentry.captureException(error);
      return;
    }

    // Get workspace names
    const enriched = await Promise.all(
      (data || []).map(async (m) => {
        const { data: ws } = await supabase
          .from("workspaces")
          .select("name")
          .eq("id", m.workspace_id)
          .single();
        return { ...m, workspace_name: ws?.name };
      })
    );

    setMemberships((prev) => ({ ...prev, [userId]: enriched }));
    setExpandedUser(userId);
  };

  const filteredProfiles = profiles.filter((p) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      p.email?.toLowerCase().includes(q) ||
      p.full_name?.toLowerCase().includes(q)
    );
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl font-bold">Users</h1>
          <p className="text-sm text-muted-foreground mt-1">{profiles.length} user{profiles.length !== 1 ? "s" : ""}</p>
        </div>
        <input
          type="text"
          placeholder="Search by email or name..."
          className="px-3 py-2 rounded-md border border-border bg-secondary text-sm w-64"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="border border-border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-muted/50 text-left">
              <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider w-8"></th>
              <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Email</th>
              <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Name</th>
              <th className="px-4 py-3 text-xs font-medium text-muted-foreground uppercase tracking-wider">Created</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {filteredProfiles.map((p) => (
              <>
                <tr
                  key={p.id}
                  onClick={() => loadUserMemberships(p.user_id)}
                  className="hover:bg-muted/30 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3">
                    {expandedUser === p.user_id ? (
                      <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="w-4 h-4 text-muted-foreground" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <UserIcon className="w-4 h-4 text-muted-foreground" />
                      <span className="text-sm">{p.email}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">{p.full_name || "—"}</td>
                  <td className="px-4 py-3 text-sm text-muted-foreground">
                    {new Date(p.created_at).toLocaleDateString()}
                  </td>
                </tr>
                {expandedUser === p.user_id && memberships[p.user_id] && (
                  <tr key={`${p.id}-memberships`}>
                    <td colSpan={4} className="px-8 py-3 bg-muted/20">
                      <p className="text-xs text-muted-foreground mb-2 uppercase tracking-wider">Workspaces</p>
                      {memberships[p.user_id].length === 0 ? (
                        <p className="text-sm text-muted-foreground">No workspace memberships</p>
                      ) : (
                        <div className="space-y-1">
                          {memberships[p.user_id].map((m) => (
                            <div key={m.id} className="flex items-center gap-2 text-sm">
                              <span>{m.workspace_name || m.workspace_id}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400">
                                {m.role}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </>
            ))}
            {filteredProfiles.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  {search ? "No users match your search." : "No users yet."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
